import React, { useEffect, useMemo, useState } from "react";
import {
  createClient,
  getAdminClients,
  updateClient,
  updateClientStatus,
} from "../services/ourClientsService";
import "./OurClientsManagement.css";

const emptyForm = {
  clientId: "",
  clientName: "",
  projectName: "",
  city: "",
  cityTag: "",
  short: "",
  description: "",
  footer: "",
  longitude: "",
  latitude: "",
};

const clientFilterOptions = ["All", "Active", "Inactive"];

const isActiveClient = (client) =>
  client.active === true || client.active === "true";

const mapApiClientToRecord = (client) => ({
  id: client.id,
  clientId: client.id,
  clientName: client.client,
  projectName: client.project,
  city: client.city,
  cityTag: client.cityTag,
  short: client.short,
  description: client.description,
  footer: client.footer,
  longitude: client.coords?.[0] ?? "",
  latitude: client.coords?.[1] ?? "",
  active: client.isActive,
  createdAt: client.createdAt,
  updatedAt: client.updatedAt,
});

const mapFormToApiClient = (formData, includeId = true) => {
  const payload = {
    client: formData.clientName.trim(),
    city: formData.city.trim(),
    cityTag: formData.cityTag.trim(),
    project: formData.projectName.trim(),
    coords: [Number(formData.longitude), Number(formData.latitude)],
    short: formData.short.trim(),
    description: formData.description.trim(),
    footer: formData.footer.trim(),
  };

  if (includeId) {
    payload.id = formData.clientId.trim();
  }

  return payload;
};

const OurClientsManagement = () => {
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [clientFilter, setClientFilter] = useState("All");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setIsLoading(true);
        setMessage("");
        const data = await getAdminClients();
        setClients(data.map(mapApiClientToRecord));
      } catch (error) {
        setMessage(
          error.response?.data?.message || "Failed to fetch client records."
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchClients();
  }, []);

  const summary = useMemo(
    () => `${clients.length} client${clients.length === 1 ? "" : "s"} saved`,
    [clients.length]
  );

  const filteredClients = useMemo(() => {
    if (clientFilter === "Active") {
      return clients.filter(isActiveClient);
    }

    if (clientFilter === "Inactive") {
      return clients.filter((client) => !isActiveClient(client));
    }

    return clients;
  }, [clientFilter, clients]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validateForm = () => {
    const nextErrors = {};

    Object.entries(formData).forEach(([key, value]) => {
      if (!String(value).trim()) {
        nextErrors[key] = "Required";
      }
    });

    const longitude = Number(formData.longitude);
    const latitude = Number(formData.latitude);

    if (formData.longitude && (Number.isNaN(longitude) || longitude < -180 || longitude > 180)) {
      nextErrors.longitude = "Use -180 to 180";
    }

    if (formData.latitude && (Number.isNaN(latitude) || latitude < -90 || latitude > 90)) {
      nextErrors.latitude = "Use -90 to 90";
    }

    if (formData.clientId && !/^[a-zA-Z0-9_-]+$/.test(formData.clientId)) {
      nextErrors.clientId = "Use letters, numbers, _ or -";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setErrors({});
    setMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);
      setMessage("");
      const savedClient = editingId
        ? await updateClient(editingId, mapFormToApiClient(formData, false))
        : await createClient(mapFormToApiClient(formData));
      const nextClient = mapApiClientToRecord(savedClient);

      setClients((current) =>
        editingId
          ? current.map((client) => (client.id === editingId ? nextClient : client))
          : [nextClient, ...current]
      );

      resetForm();
    } catch (error) {
      setMessage(
        error.response?.data?.message ||
          error.response?.data?.errors?.join(", ") ||
          "Failed to save client."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (client) => {
    setEditingId(client.id);
    setFormData({
      clientId: client.clientId || client.id || "",
      clientName: client.clientName,
      projectName: client.projectName,
      city: client.city,
      cityTag: client.cityTag,
      short: client.short,
      description: client.description,
      footer: client.footer,
      longitude: client.longitude || "",
      latitude: client.latitude || "",
    });
    setErrors({});
  };

  const handleStatusChange = async (clientId, active) => {
    try {
      setMessage("");
      const updatedClient = await updateClientStatus(clientId, active);
      const nextClient = mapApiClientToRecord(updatedClient);
      setClients((current) =>
        current.map((client) =>
          client.id === clientId ? nextClient : client
        )
      );
    } catch (error) {
      setMessage(
        error.response?.data?.message || "Failed to update client status."
      );
    }
  };

  return (
    <main className="our-clients-admin-page">
      <section className="clients-admin-hero">
        <div>
        
          <h1>Our Clients</h1>
          <span>
            Add and manage client work details shown to the team. Coordinates
            use decimal degrees: longitude first, latitude second.
          </span>
        </div>
        <strong>{summary}</strong>
      </section>

      <section className="clients-admin-shell">
        <form className="clients-admin-form" onSubmit={handleSubmit}>
          <div className="clients-admin-card-head">
            <h2>{editingId ? "Edit Client" : "Add Client"}</h2>
            {editingId && (
              <button type="button" onClick={resetForm}>
                Cancel Edit
              </button>
            )}
          </div>

          <div className="clients-admin-grid">
            <label>
              <span>Client ID</span>
              <input
                name="clientId"
                value={formData.clientId}
                onChange={handleChange}
                disabled={Boolean(editingId)}
              />
              {errors.clientId && <em>{errors.clientId}</em>}
            </label>

            <label>
              <span>Client</span>
              <input
                name="clientName"
                value={formData.clientName}
                onChange={handleChange}
              />
              {errors.clientName && <em>{errors.clientName}</em>}
            </label>

            <label>
              <span>Project</span>
              <input
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
              />
              {errors.projectName && <em>{errors.projectName}</em>}
            </label>

            <label>
              <span>City</span>
              <input
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
              {errors.city && <em>{errors.city}</em>}
            </label>

            <label>
              <span>City Tag</span>
              <input
                name="cityTag"
                value={formData.cityTag}
                onChange={handleChange}
              />
              {errors.cityTag && <em>{errors.cityTag}</em>}
            </label>

            <label>
              <span>Footer</span>
              <input
                name="footer"
                value={formData.footer}
                onChange={handleChange}
              />
              {errors.footer && <em>{errors.footer}</em>}
            </label>

            <label>
              <span>Longitude</span>
              <input
                name="longitude"
                value={formData.longitude}
                onChange={handleChange}
                placeholder="Example: 77.5946"
                type="number"
                step="any"
              />
              {errors.longitude && <em>{errors.longitude}</em>}
            </label>

            <label>
              <span>Latitude</span>
              <input
                name="latitude"
                value={formData.latitude}
                onChange={handleChange}
                placeholder="Example: 12.9716"
                type="number"
                step="any"
              />
              {errors.latitude && <em>{errors.latitude}</em>}
            </label>
          </div>

          <label>
            <span>Short</span>
            <textarea
              name="short"
              value={formData.short}
              onChange={handleChange}
              rows="2"
            />
            {errors.short && <em>{errors.short}</em>}
          </label>

          <label>
            <span>Description</span>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="2"
            />
            {errors.description && <em>{errors.description}</em>}
          </label>

          <div className="clients-admin-actions">
            <button type="submit" disabled={isSaving}>
              {editingId ? "Update Client" : "Add Client"}
            </button>
            <button type="button" onClick={resetForm}>
              Clear
            </button>
          </div>
        </form>

        <section className="clients-admin-list">
          <div className="clients-admin-card-head">
            <h2>Client Records</h2>
            <div className="clients-admin-filter" aria-label="Filter client records">
              {clientFilterOptions.map((option) => (
                <button
                  className={clientFilter === option ? "active" : ""}
                  key={option}
                  onClick={() => setClientFilter(option)}
                  type="button"
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {message && <div className="clients-admin-empty">{message}</div>}

          {isLoading ? (
            <div className="clients-admin-empty">Loading clients...</div>
          ) : clients.length === 0 ? (
            <div className="clients-admin-empty">
              No clients added yet.
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="clients-admin-empty">
              No {clientFilter.toLowerCase()} clients found.
            </div>
          ) : (
            <div className="clients-admin-records">
              {filteredClients.map((client) => (
                <article className="clients-admin-record" key={client.id}>
                  <div>
                    <h3>{client.clientName}</h3>
                    <p>{client.projectName}</p>
                    {/* <span>ID: {client.clientId || client.id}</span> */}
                    <span>{client.city}</span>
                    {/* <span>{client.cityTag}</span> */}
                  </div>
                  <p>{client.short}</p>
                  <div className="clients-admin-record-meta">
                    <span>
                      Lng {client.longitude} / Lat {client.latitude}
                    </span>
                    <div className="clients-admin-status-options">
                      <label>
                        <input
                          checked={isActiveClient(client)}
                          onChange={() => handleStatusChange(client.id, true)}
                          type="checkbox"
                        />
                        Active
                      </label>
                      <label>
                        <input
                          checked={!isActiveClient(client)}
                          onChange={() => handleStatusChange(client.id, false)}
                          type="checkbox"
                        />
                        Inactive
                      </label>
                    </div>
                  </div>
                  <small>{client.footer}</small>
                  <div className="clients-admin-record-actions">
                    <button type="button" onClick={() => handleEdit(client)}>
                      Edit
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </section>
    </main>
  );
};

export default OurClientsManagement;
